import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FiMenu, FiX } from 'react-icons/fi';
import axios from 'axios';
import BookStats from '../BookStats';
import Logo from '../LogoComponent';
import { useNotification } from '../../Context/NotificationContext';
import A1 from '../../assets/AddProfileImgs/A1.png';
import A2 from '../../assets/AddProfileImgs/A2.png';
import A3 from '../../assets/AddProfileImgs/A3.png';
import A4 from '../../assets/AddProfileImgs/A4.png';
import A5 from '../../assets/AddProfileImgs/A5.png';
import A6 from '../../assets/AddProfileImgs/A6.png';
import A7 from '../../assets/AddProfileImgs/A7.png';
import A8 from '../../assets/AddProfileImgs/A8.png';
import A9 from '../../assets/AddProfileImgs/A9.png';
import './style.css';

const config = require('../../Helpers/config.json');
const avatars = [A1, A2, A3, A4, A5, A6, A7, A8, A9];
const emptyProfile = { name: '', dob: '', gender: 'm', code: '', image: '' };
const dateValue = value => value ? String(value).split('T')[0] : '';

export default function Profile() {
  const location = useLocation();
  const { showNotification } = useNotification();
  const [profile, setProfile] = useState(emptyProfile);
  const [savedProfile, setSavedProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [stats, setStats] = useState({});
  const profileId = location.state?.profile?.profile_id || localStorage.getItem('profileId');
  const user = JSON.parse(localStorage.getItem('data') || 'null');
  const uid = user?.uid || localStorage.getItem('id');
  const isVerified = Boolean(location.state?.isVerified);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setStats({});
    setLoadError(false);
    fetch(`${config.baseUrl2}profile/getProfile?profile_id=${profileId}&uid=${uid}`)
      .then(res => { if (!res.ok) throw new Error('Profile request failed'); return res.json(); })
      .then(res => {
        if (!res.profile) throw new Error('Profile unavailable');
        if (!current) return;
        const next = { name: res.profile.name || '', dob: dateValue(res.profile.dob), gender: res.profile.gender || 'm', code: res.profile.code === 'Not Available' ? '' : res.profile.code || '', image: res.profile.image || '' };
        setProfile(next);
        setSavedProfile(next);
      })
      .catch(() => { if (current) setLoadError(true); })
      .finally(() => { if (current) setLoading(false); });
    const endpoints = { books: 'getbooksRead', languages: 'getBooksLanguageRead', vocabulary: 'getwordsLookup', translations: 'getBookTranslationChecked' };
    Object.entries(endpoints).forEach(([key, endpoint]) => {
      axios.get(`${config.baseUrl2}stats/books/${endpoint}/?userId=${uid}&profileId=${profileId}`)
        .then(res => { if (current) setStats(previous => ({ ...previous, [key]: res.data?.data })); })
        .catch(() => { if (current) setStats(previous => ({ ...previous, [key]: null })); });
    });
    return () => { current = false; };
  }, [profileId, uid]);

  const change = event => setProfile(previous => ({ ...previous, [event.target.name]: event.target.value }));
  const dirty = JSON.stringify(profile) !== JSON.stringify(savedProfile);
  const avatarSrc = image => {
    if (!image) return A4;
    if (avatars.includes(image)) return image;
    const filename = image.split('/').pop().split('.')[0];
    return `${config.baseUrl2Image}/resources/static/assets/uploads/profiles/${filename}.png`;
  };
  const save = async event => {
    event.preventDefault();
    if (!isVerified || saving || !dirty) return;
    setSaving(true);
    try {
      const response = await fetch(`${config.baseUrl2}profile/updateProfile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, profile_id: profileId, name: profile.name.trim(), dob: profile.dob, gender: profile.gender, code: profile.code || null, image: profile.image, categories: '1' })
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Please try again.');
      setSavedProfile(profile);
      localStorage.setItem('image', profile.image);
      showNotification({ title: 'Profile saved', message: 'Your profile has been updated.', type: 'success' });
    } catch (error) {
      showNotification({ title: 'Unable to save profile', message: error.message, type: 'error' });
    } finally { setSaving(false); }
  };

  return <div className="reader-profile">
    <header className="reader-header">
      <Link to="/dashboard" aria-label="FictionGenie stories"><Logo color="white" width="66px" /></Link>
      <button className="reader-menu-toggle" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={menuOpen} aria-controls="reader-navigation" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <FiX /> : <FiMenu />}</button>
      <nav id="reader-navigation" className={menuOpen ? 'reader-navigation is-open' : 'reader-navigation'} aria-label="Main navigation">
        <NavLink to="/dashboard">Stories</NavLink><NavLink to="/profile" end>My Profile</NavLink><NavLink to="/select-profile">Switch Profile</NavLink>
      </nav>
    </header>
    <main className="reader-main">
      <div className="reader-intro"><h1>My reading profile</h1><p>A little about you. A world of stories ahead.</p></div>
      {loading ? <div className="reader-state" role="status">Loading your profile…</div> : loadError ? <div className="reader-state" role="alert">We couldn’t load your profile. Please refresh to try again.</div> : <div className="reader-profile-grid">
        <section className="reader-avatar-card" aria-label="Your avatar">
          <img className="reader-avatar" src={avatarSrc(profile.image)} alt={`${profile.name}'s avatar`} />
          <h2>{profile.name}</h2><p>Your story starts here.</p>
          <button className="reader-button reader-button-outline" disabled={!isVerified || saving} onClick={() => { setPendingAvatar(profile.image); setAvatarOpen(true); }}>Change avatar</button>
        </section>
        <section className="reader-details" aria-labelledby="profile-details-title">
          <h2 id="profile-details-title">Profile details</h2><p>Make this space your own.</p>
          <form onSubmit={save}>
            <fieldset disabled={!isVerified || saving} className="reader-fields">
              <div><label htmlFor="reader-name">Your name</label><input id="reader-name" name="name" value={profile.name} onChange={change} required maxLength={100} autoComplete="given-name" /></div>
              <div><label htmlFor="reader-dob">Date of birth</label><input id="reader-dob" name="dob" type="date" value={profile.dob} onChange={change} required max={new Date().toLocaleDateString('en-CA')} /></div>
              <div><label htmlFor="reader-gender">Gender</label><select id="reader-gender" name="gender" value={profile.gender} onChange={change}><option value="m">Male</option><option value="f">Female</option></select></div>
              <div><label htmlFor="reader-code">Profile code</label><input id="reader-code" name="code" type="password" value={profile.code} onChange={change} placeholder="Enter code" autoComplete="new-password" /></div>
            </fieldset>
            {!isVerified && <p className="reader-verification">To edit this profile, verify parent access from <Link to="/account">Account</Link>.</p>}
            <div className="reader-form-actions"><button type="button" className="reader-button reader-button-quiet" disabled={!dirty || saving} onClick={() => setProfile(savedProfile)}>Cancel</button><button type="submit" className="reader-button reader-button-yellow" disabled={!isVerified || !dirty || saving}>{saving ? 'Saving…' : 'Save changes'}</button></div>
          </form>
        </section>
      </div>}
      <BookStats bookReadStats={stats.books === null ? null : stats.books === undefined ? undefined : stats.books?.[0]?.totalBooksRead || 0} bookLanguageReadStats={stats.languages} bookTranslationCheckedStats={stats.translations} wordsLookUpStats={stats.vocabulary} profileId={profileId} userId={uid} />
    </main>
    <Modal show={avatarOpen} onHide={() => setAvatarOpen(false)} centered scrollable fullscreen="sm-down" className="reader-dialog" aria-labelledby="avatar-dialog-title">
      <Modal.Header closeButton><Modal.Title id="avatar-dialog-title">Choose your avatar</Modal.Title></Modal.Header>
      <Modal.Body><p>Pick a character for your next adventure.</p><div className="reader-avatar-options">{avatars.map((image, index) => <button key={image} className={pendingAvatar === image ? 'is-selected' : ''} aria-label={`Avatar ${index + 1}`} aria-pressed={pendingAvatar === image} onClick={() => setPendingAvatar(image)}><img src={image} alt="" />{pendingAvatar === image && <span aria-hidden="true">✓</span>}</button>)}</div></Modal.Body>
      <Modal.Footer><button className="reader-button reader-button-quiet" onClick={() => setAvatarOpen(false)}>Cancel</button><button className="reader-button reader-button-yellow" onClick={() => { setProfile(previous => ({ ...previous, image: pendingAvatar })); setAvatarOpen(false); }}>Use avatar</button></Modal.Footer>
    </Modal>
  </div>;
}
